import { DestinationType, ExistingDestination, PreparedDestination } from "@prequel/react";

import axios, { ApiResponse, ApiRoutes, ID_PLACEHOLDER, TestConnectionResult } from "@/api/client";
import {
  createResource,
  deleteResource,
  getAllPages,
  getResource,
  getResourcePage,
  handleApiError,
  updateResource,
} from "@/api/helpers";
import { ExistingDestinationPayload, ExistingModel, PaginationParams, PaginationResponse } from "@/types";

const getDestination = async (destinationID: ExistingDestination["id"]): Promise<ExistingDestination> =>
  getResource<ExistingDestination, { destination: ExistingDestination }>(
    `${ApiRoutes.DESTINATIONS}/${destinationID}`,
    (response) => ({
      ...response.data.data.destination,
      type: DestinationType.ExistingDestination,
    }),
    "Failed to fetch destination.",
  );

const getDestinations = async (
  paginationParams: PaginationParams,
  filters: Record<string, string> = {},
): Promise<PaginationResponse<ExistingDestination>> => {
  return getResourcePage<ExistingDestination, { destinations: ExistingDestination[] | null }>(
    ApiRoutes.DESTINATIONS,
    paginationParams,
    (response) =>
      response.data.data.destinations?.map((s) => ({
        ...s,
        type: DestinationType.ExistingDestination,
      })) ?? [],
    "Failed to fetch destinations.",
    filters,
  );
};

const getAllDestinations = async (): Promise<ExistingDestination[]> => getAllPages(getDestinations);

const testNewDestination: (d: PreparedDestination) => Promise<TestConnectionResult> = (destination) =>
  axios
    .post(ApiRoutes.TEST_DESTINATION, { destination })
    .then((response: ApiResponse<TestConnectionResult>) => ({
      status: response.data.data?.status,
      message: response.data.message,
    }))
    .catch(handleApiError("Destination connection test failed.", true));

const testExistingDestination: (p: ExistingDestinationPayload) => Promise<TestConnectionResult> = ({
  destinationID,
  fields,
}) => {
  const url = ApiRoutes.TEST_EXISTING_DESTINATION.replace(ID_PLACEHOLDER, destinationID);
  return axios
    .post(url, fields ? { destination: fields } : undefined)
    .then((response: ApiResponse<TestConnectionResult>) => ({
      status: response.data.data?.status,
      message: response.data.message,
    }))
    .catch(handleApiError("Destination connection test failed.", true));
};

const postDestination = (destination: PreparedDestination): Promise<ExistingDestination> =>
  createResource<PreparedDestination, ExistingDestination>(
    ApiRoutes.DESTINATIONS,
    destination,
    "destination",
    "destination",
    "Destination creation failed.",
  );

const transferDestination: ({
  destinationID,
  fullRefresh,
}: {
  destinationID: ExistingDestination["id"];
  fullRefresh: boolean;
}) => Promise<{ transfer_id: string }> = ({ destinationID, fullRefresh }) =>
  axios
    .post(`${ApiRoutes.DESTINATIONS}/${destinationID}${ApiRoutes.TRANSFER}`, {
      full_refresh: fullRefresh,
    })
    .then((response: ApiResponse<{ transfer_id: string }>) => response.data.data)
    .catch(handleApiError("Transfer failed to enqueue."));

const patchDestination = (
  destinationID: ExistingDestination["id"],
  fields: Partial<PreparedDestination>,
): Promise<ExistingDestination> =>
  updateResource<PreparedDestination, ExistingDestination>(
    `${ApiRoutes.DESTINATIONS}/${destinationID}`,
    fields,
    "destination",
    "destination",
    "Failed to update destination.",
  );

const deleteDestination = (destinationID: ExistingDestination["id"]): Promise<void> =>
  deleteResource(`${ApiRoutes.DESTINATIONS}/${destinationID}`, "Failed to delete destination.");

const getDestinationsByRecipient = async (
  recipientId: string,
  paginationParams: PaginationParams = {},
): Promise<PaginationResponse<ExistingDestination>> =>
  getResourcePage<ExistingDestination, { destinations: ExistingDestination[] | null }>(
    ApiRoutes.DESTINATIONS,
    paginationParams,
    (response) =>
      response.data.data.destinations?.map((s) => ({
        ...s,
        type: DestinationType.ExistingDestination,
      })) ?? [],
    "Failed to fetch destinations for recipient.",
    { recipient_id: recipientId },
  );

const getDestinationModels = async (destinationID: ExistingDestination["id"]): Promise<ExistingModel[]> =>
  getResource<ExistingModel[], { models: ExistingModel[] }>(
    ApiRoutes.DESTINATION_MODELS.replace(ID_PLACEHOLDER, destinationID),
    (response) => response.data.data.models,
    "Failed to fetch destination models.",
  );

const DestinationsService = {
  getDestinations,
  getAllDestinations,
  getDestination,
  getDestinationsByRecipient,
  getDestinationModels,
  testNewDestination,
  testExistingDestination,
  postDestination,
  transferDestination,
  patchDestination,
  deleteDestination,
};
export default DestinationsService;
